import * as chrono from 'chrono-node';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as linkify from 'linkifyjs';

type RegionType = 'phone' | 'email' | 'date' | 'url';

interface RegionMatch {
    type: RegionType;
    text: string;
    index: number;
    parsedDate?: Date;
    href?: string;
}

export interface IRegion {
    startIndex: number;
    length: number;
    parsedDate: Date | null;
    key: string;
    text: string;
    type?: RegionType;
    href?: string;
}

export const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
export const PHONE_REGEX = /((\+?\d{1,3}|\d{1,3})[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;

const ALLOWED_URL_PROTOCOLS = new Set(['http:', 'https:']);
const MATCH_PRECEDENCE: Record<Exclude<RegionType, 'url'>, number> = {
    email: 0,
    phone: 1,
    date: 2,
};

function findUrls(line: string): RegionMatch[] {
    return linkify.find(line, 'url').flatMap((match): RegionMatch[] => {
        try {
            if (!ALLOWED_URL_PROTOCOLS.has(new URL(match.href).protocol)) {
                return [];
            }
        } catch {
            return [];
        }

        return [{
            type: 'url',
            text: match.value,
            index: match.start,
            href: match.href,
        }];
    });
}

function findMatchesInGap(text: string, offset: number): RegionMatch[] {
    const candidates: RegionMatch[] = [];

    for (const match of text.matchAll(EMAIL_REGEX)) {
        candidates.push({
            type: 'email',
            text: match[0],
            index: offset + match.index,
        });
    }

    for (const match of text.matchAll(PHONE_REGEX)) {
        const parsedPhone = parsePhoneNumberFromString(match[0], 'US');
        if (parsedPhone?.isValid()) {
            candidates.push({
                type: 'phone',
                text: match[0],
                index: offset + match.index,
            });
        }
    }

    for (const dateRange of chrono.parse(text)) {
        const parsedDate = dateRange.date();

        if (!dateRange.text.toLowerCase().includes('am') && !dateRange.text.toLowerCase().includes('pm')) {
            const hour = parsedDate.getHours();
            if (hour <= 6) {
                parsedDate.setHours(hour + 12);
            }
        }

        candidates.push({
            type: 'date',
            text: dateRange.text,
            index: offset + dateRange.index,
            parsedDate,
        });
    }

    candidates.sort((a, b) => {
        const precedence = MATCH_PRECEDENCE[a.type as Exclude<RegionType, 'url'>] -
            MATCH_PRECEDENCE[b.type as Exclude<RegionType, 'url'>];
        return precedence || a.index - b.index;
    });

    const accepted: RegionMatch[] = [];
    for (const candidate of candidates) {
        const candidateEnd = candidate.index + candidate.text.length;
        const overlaps = accepted.some((match) => (
            candidate.index < match.index + match.text.length && match.index < candidateEnd
        ));
        if (!overlaps) {
            accepted.push(candidate);
        }
    }

    return accepted;
}

export function parseMessageLineIntoRegions(line: string, lineIndex: number): IRegion[] {
    const urls = findUrls(line).sort((a, b) => a.index - b.index);
    const matches = [...urls];
    let gapStart = 0;

    for (const url of urls) {
        if (url.index > gapStart) {
            matches.push(...findMatchesInGap(line.slice(gapStart, url.index), gapStart));
        }
        gapStart = url.index + url.text.length;
    }

    if (gapStart < line.length) {
        matches.push(...findMatchesInGap(line.slice(gapStart), gapStart));
    }

    matches.sort((a, b) => a.index - b.index);

    const regions: IRegion[] = [];
    let cursor = 0;
    let regionIndex = 0;

    for (const match of matches) {
        if (match.index > cursor) {
            const text = line.slice(cursor, match.index);
            regions.push({
                key: `line-${lineIndex}-text-${regionIndex++}`,
                text,
                startIndex: cursor,
                length: text.length,
                parsedDate: null,
            });
        }

        regions.push({
            key: `line-${lineIndex}-${match.type}-${regionIndex++}`,
            text: match.text,
            startIndex: match.index,
            length: match.text.length,
            parsedDate: match.parsedDate ?? null,
            type: match.type,
            href: match.href,
        });
        cursor = match.index + match.text.length;
    }

    if (cursor < line.length || regions.length === 0) {
        const text = line.slice(cursor);
        regions.push({
            key: `line-${lineIndex}-text-${regionIndex}`,
            text,
            startIndex: cursor,
            length: text.length,
            parsedDate: null,
        });
    }

    return regions;
}
